"use client";

import Translations from "@translate/components/OnboardingWizard/Paywall/OnboardingPaywallFreeTrialContent";
import type { CSSProperties, FC } from "react";
import { AppImage } from "@components/NextImage";
import { useEffect } from "react";
import { getOnboardingV3Session } from "@/src/hooks/onboarding/onboardingV3SessionStorage";
import { Icon, ospThemes } from "@ospweb/sdk-ui";
import { cn, dayjs } from "utils";
import { I18nUtil } from "@utils/i18n";
import { agreement } from "@const/agreement";
import { useBridge } from "@/src/lib/bridge/useBridge";
import { MessageType } from "@/src/lib/bridge/types";
import {
  trackObPaywallDismiss,
  trackObPaywallView,
} from "@/src/hooks/onboarding/onboardingAnalytics";
import { gaTrackingAttrs } from "@utils/tracking";
import { buildOpenReplayLabel } from "@utils/openreplay";
import { ONBOARDING_PAYWALL_COPY_OVERRIDES } from "@components/ChatOnboarding/constants/onboardingPaywallCopy";
import { OnboardingFirstPaywallContent } from "./OnboardingFirstPaywallContent";
import { OnboardingPaywallAnnualCard } from "./OnboardingPaywallAnnualCard";
import { GoalActivationPaywallCarousel } from "./GoalActivationPaywallCarousel";
import {
  ONBOARDING_PAYWALL_FREE_TRIAL_FIGMA_NODE,
  ONBOARDING_PAYWALL_FREE_TRIAL_PC_FALLBACK_NODE,
  ONBOARDING_PAYWALL_FOOTER_CLASS,
  ONBOARDING_PAYWALL_SCRIM_GRADIENT,
  ONBOARDING_PAYWALL_SCROLL_CLASS,
  ONBOARDING_PAYWALL_STATUS_BAR_FALLBACK_PX,
  ONBOARDING_PAYWALL_TRIAL_DAYS,
  resolveOnboardingPaywallHeroImage,
} from "./onboardingPaywallFreeTrialTokens";
import { useOnboardingPaywallPurchase } from "./useOnboardingPaywallPurchase";
import type { OnboardingPaywallFreeTrialContentProps } from "./types";

const I18n = I18nUtil.createScoped(Translations);

/** Figma / copy key still uses literal `[Date, Time]`; inject runtime end at render. */
const FREE_TRIAL_DISCLAIMER_KEY =
  "Your free trial ends on [Date, Time]. On that date, your subscription will auto-renew at $299.99/year unless you cancel at least 24 hours beforehand. No charges if canceled within 7 days.";

const formatFreeTrialEndsAt = (from = dayjs()) =>
  from.add(ONBOARDING_PAYWALL_TRIAL_DAYS, "day").format("MMM D, YYYY");

const buildFreeTrialDisclaimer = () =>
  I18n.t(FREE_TRIAL_DISCLAIMER_KEY).replace(
    "[Date, Time]",
    formatFreeTrialEndsAt(),
  );

const LegacyOnboardingPaywallFreeTrialContent: FC<
  OnboardingPaywallFreeTrialContentProps
> = ({
  isPc = false,
  onClose,
  trackingModuleName,
  paywallCopyOverrides,
  analyticsSource,
  onWebCheckoutRedirect,
  paywallVariant,
}) => {
  const { send, isInApp } = useBridge();
  const copy = paywallCopyOverrides ?? ONBOARDING_PAYWALL_COPY_OVERRIDES;
  const analyticsSourceValue = analyticsSource ?? "onboarding";
  const isGoalActivationPaywall = paywallVariant === "goal_activate_second";
  const isSecondPaywall = analyticsSourceValue === "goal_activate";
  const {
    annualDisplayPrice,
    buttonText,
    isBuyTrialNow,
    isLoading,
    handlePurchase,
  } = useOnboardingPaywallPurchase({
    onClose,
    trackingModuleName,
    analyticsSource: analyticsSourceValue,
    onWebCheckoutRedirect,
  });

  // 广告支才有 band（后端 RC-8：非 ai_job_risk 场景整体不返回该字段），
  // 所以「有值 = 广告支入口」，不用额外判场景。
  const adBand = getOnboardingV3Session()?.band ?? null;

  useEffect(() => {
    trackObPaywallView(analyticsSourceValue, adBand);
  }, [analyticsSourceValue, adBand]);

  const openExternalUrl = (url: string) => {
    if (isInApp) {
      send(MessageType.OPEN_URL_EXTERNAL, { url });
      return;
    }
    window.open(url, "_blank");
  };

  const handleClose = () => {
    trackObPaywallDismiss(analyticsSourceValue);
    onClose?.();
  };

  return (
    <div
      className="flex h-full min-h-0 flex-col overflow-hidden bg-background_solid_neutral_secondary"
      data-testid="onboarding-paywall-modal"
      data-figma-node={ONBOARDING_PAYWALL_FREE_TRIAL_FIGMA_NODE}
      data-figma-node-pc={
        isPc ? ONBOARDING_PAYWALL_FREE_TRIAL_PC_FALLBACK_NODE : undefined
      }
      data-figma-fallback={
        isPc ? ONBOARDING_PAYWALL_FREE_TRIAL_PC_FALLBACK_NODE : undefined
      }
      {...gaTrackingAttrs(
        isSecondPaywall ? "paywall2_view" : "ob_paywall_view",
        isSecondPaywall
          ? { source: "put_ed_to_work" }
          : { source: analyticsSourceValue },
      )}
      data-or-view="ob_paywall_view"
      {...(adBand ? { "data-or-param-band": adBand } : {})}
      data-or-param-paywall_seq={isSecondPaywall ? "2" : "1"}
      data-or-param-source={
        isSecondPaywall ? "put_ed_to_work" : analyticsSourceValue
      }
    >
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <AppImage
            src={
              isGoalActivationPaywall
                ? "/assets/images/goal-paywall-background.png"
                : resolveOnboardingPaywallHeroImage(isPc)
            }
            alt=""
            width={isPc ? 1440 : 786}
            height={isPc ? 2148 : 1704}
            loading="eager"
            data-testid="paywall-hero-background"
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: isGoalActivationPaywall
                ? "linear-gradient(180deg, rgba(30, 21, 11, 0) 0%, rgba(30, 21, 11, 0.08) 17.516%, rgba(30, 21, 11, 0.26) 30.991%, rgba(30, 21, 11, 0.5) 43.117%, rgba(30, 21, 11, 0.72) 53.897%, rgba(30, 21, 11, 0.86) 61.981%, rgba(30, 21, 11, 0.92) 67.371%)"
                : ONBOARDING_PAYWALL_SCRIM_GRADIENT,
            }}
          />
        </div>

        <header
          className={cn("relative z-20 shrink-0 px-4", isPc && "pt-6")}
          style={
            !isPc
              ? ({
                  paddingTop: `max(var(--safe-area-inset-top, 0px), ${ONBOARDING_PAYWALL_STATUS_BAR_FALLBACK_PX}px)`,
                } as CSSProperties)
              : undefined
          }
          data-testid="paywall-top-nav"
        >
          <div className="relative flex items-center justify-center py-2">
            <button
              type="button"
              aria-label="close"
              data-testid="paywall-close-button"
              data-openreplay-label={
                isGoalActivationPaywall
                  ? "goal_activation_paywall_close"
                  : buildOpenReplayLabel("Onboarding", "Paywall", "Close")
              }
              className={cn(
                "absolute flex items-center justify-center",
                isPc
                  ? "right-0 top-0 size-6"
                  : "left-0 top-1/2 size-10 -translate-y-1/2 rounded-full bg-background_solid_neutral_primary p-2",
              )}
              onClick={handleClose}
            >
              <Icon
                name="close"
                size={24}
                color={
                  isPc
                    ? ospThemes.colors.content_inverse_primary
                    : ospThemes.colors.content_primary
                }
              />
            </button>
            {/*
              EDG-525 ③：PC 下 logo 左对齐（Figma `98034:230232`），只有 App 帧
              （`97553:361693`）是居中。`left-4` + header 的 `px-4` = 距 hero 左 32px，
              与已对齐同一张稿的 `PaywallModalContent` 的 `left-8` 一致。
            */}
            <Icon
              name="ed-logo"
              size={isPc ? 32 : 24}
              color={ospThemes.colors.content_inverse_primary}
              className={cn(isPc && "absolute left-4 top-1/2 -translate-y-1/2")}
            />
          </div>
        </header>

        <div
          className={cn(
            ONBOARDING_PAYWALL_SCROLL_CLASS,
            "px-4",
            isGoalActivationPaywall && isPc ? "pb-0" : "pb-6",
          )}
          data-testid="paywall-scroll-view"
        >
          {isGoalActivationPaywall ? (
            <GoalActivationPaywallCarousel isPc={isPc} />
          ) : (
            <div className="flex flex-col gap-6 pt-4">
              <div className="flex flex-col gap-2">
                <h1
                  className="headline_regular_l text-content_inverse_primary"
                  data-testid="paywall-title"
                >
                  {copy.title}
                </h1>
                {copy.subtitle ? (
                  <p className="body_medium_l text-content_inverse_primary">
                    {copy.subtitle}
                  </p>
                ) : null}
              </div>

              <ul
                className="flex flex-col gap-2"
                data-testid="paywall-feature-list"
              >
                {(copy.features ?? []).map((feature) => (
                  <li key={feature} className="flex items-start gap-2">
                    <Icon
                      name="check"
                      size={16}
                      className="mt-1 shrink-0 text-content_inverse_primary"
                    />
                    <span className="body_l text-content_inverse_primary">
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      <footer
        className={cn(ONBOARDING_PAYWALL_FOOTER_CLASS, "px-4 pt-6")}
        data-testid="paywall-pricing-footer"
      >
        <OnboardingPaywallAnnualCard annualPrice={annualDisplayPrice} />

        <div className="mt-6 flex flex-col gap-2">
          {isGoalActivationPaywall ? (
            <button
              type="button"
              aria-label="Paywall primary CTA"
              data-testid="paywall-primary-cta"
              data-openreplay-label="goal_activation_paywall_purchase"
              className="button button_m emphasis_button md_button group h-11 w-full rounded-2 bg-background_accent4_overlay100 hover:bg-background_accent4_overlay50"
              disabled={isLoading}
              data-loading={isLoading}
              onClick={() => handlePurchase()}
            >
              <span className="group-loading:hidden">
                {!isLoading ? buttonText : null}
              </span>
              <Icon
                name="ic_loading2"
                size={20}
                color={ospThemes.colors.content_inverse_primary}
                className="hidden animate-spin group-loading:block"
              />
            </button>
          ) : (
            <button
              type="button"
              aria-label="Paywall primary CTA"
              data-testid="paywall-primary-cta"
              data-openreplay-label={buildOpenReplayLabel(
                "Onboarding",
                "Paywall",
                "Trial",
              )}
              className="button button_m emphasis_button md_button group h-11 w-full rounded-2 bg-background_accent4_overlay100 hover:bg-background_accent4_overlay50"
              disabled={isLoading}
              data-loading={isLoading}
              onClick={() => handlePurchase()}
            >
              <span className="group-loading:hidden">
                {!isLoading ? buttonText : null}
              </span>
              <Icon
                name="ic_loading2"
                size={20}
                color={ospThemes.colors.content_inverse_primary}
                className="hidden animate-spin group-loading:block"
              />
            </button>
          )}

          <div className="pb-4 text-center body_s text-content_quaternary">
            {isBuyTrialNow ? (
              <p data-testid="paywall-trial-disclaimer">
                {buildFreeTrialDisclaimer()}
              </p>
            ) : null}
            <p>
              <br />
              {I18n.t("By subscribing, you agree to our")}{" "}
              <button
                type="button"
                onClick={() => openExternalUrl(agreement.termsLink)}
                className="underline"
              >
                {I18n.t("Terms of Use")}
              </button>{" "}
              {I18n.t("and")}{" "}
              <button
                type="button"
                onClick={() => openExternalUrl(agreement.privacyLink)}
                className="underline"
              >
                {I18n.t("Privacy Policy")}
              </button>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export const OnboardingPaywallFreeTrialContent: FC<
  OnboardingPaywallFreeTrialContentProps
> = (props) =>
  props.onboardingFirstWall ? (
    <OnboardingFirstPaywallContent {...props} />
  ) : (
    <LegacyOnboardingPaywallFreeTrialContent {...props} />
  );
